/**
 * Optimise remote project/fundraiser images on the fly.
 *
 * Many `hero_image` / `gallery` URLs in the database point at the legacy
 * WordPress origin (`https://www.raisesrilanka.org/resources/...`) and are
 * delivered as oversized JPEGs (100–200 KB each, often 1120×840 served at
 * 380×285). Lighthouse flagged ~280 KiB of avoidable payload from these.
 *
 * We route those URLs through wsrv.nl — a free, Cloudflare-backed image
 * resizing/CDN proxy — to:
 *   - re-encode JPEG → WebP
 *   - resize to the actual displayed width
 *   - apply sane quality
 *
 * Local Vite assets, Supabase Storage URLs, and data URIs are returned as-is.
 */

interface Opts {
  width: number;
  /** Optional explicit height in pixels for fixed-aspect crops. */
  height?: number;
  /** 1-100, defaults to 78 which is visually lossless for photos. */
  quality?: number;
}

const PROXY_HOSTS = ["www.raisesrilanka.org", "raisesrilanka.org"];

// Temporary branded placeholder. The legacy WordPress origin that used to host
// these images (raisesrilanka.org/resources/...) now resolves to this Lovable
// app and returns 404 for every asset path. Until the originals are migrated
// to Cloud Storage, any reference to that origin is swapped for the banner.
const PLACEHOLDER = new URL("../assets/project-placeholder.jpg", import.meta.url).href;

export function optimizedImage(src: string | null | undefined, _opts: Opts): string {
  if (!src) return "";
  if (src.startsWith("/") || src.startsWith("blob:") || src.startsWith("data:")) return src;

  let host = "";
  try {
    host = new URL(src).host;
  } catch {
    return src;
  }

  if (PROXY_HOSTS.includes(host)) return PLACEHOLDER;

  return src;
}

